import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AppSvgIconComponent, ToastService } from '@vallift/ngl';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';

interface SystemVariableDocRow {
  key: string;
  description: string;
  exampleLabel: string;
}

interface SystemVariableSection {
  title: string;
  description: string;
  variables: SystemVariableDocRow[];
}

@Component({
  selector: 'app-system-variables-docs-page',
  standalone: true,
  imports: [AppSvgIconComponent, PageHeaderComponent],
  templateUrl: './system-variables-docs-page.html',
  styleUrl: './system-variables-docs-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemVariablesDocsPage {
  private readonly toasterService = inject(ToastService);

  readonly sections: SystemVariableSection[] = [
    {
      title: 'Date and time',
      description:
        'Values resolved from the current date and time when the form runs.',
      variables: [
        {
          key: 'now',
          description: 'Current date and time at evaluation.',
          exampleLabel: '2026-03-21, 14:30:00',
        },
        {
          key: 'today',
          description: 'Current calendar date.',
          exampleLabel: '2026-03-21',
        },
        {
          key: 'currentYear',
          description: 'Four-digit year (for example, 2026).',
          exampleLabel: '2026',
        },
        {
          key: 'currentMonth',
          description: 'Month number from 1 to 12.',
          exampleLabel: '3',
        },
        {
          key: 'currentDay',
          description: 'Day of the month from 1 to 31.',
          exampleLabel: '21',
        },
        {
          key: 'currentWeekDay',
          description: 'Weekday name (for example, Monday).',
          exampleLabel: 'Friday',
        },
        {
          key: 'timeZone',
          description: 'IANA time zone for the workspace or respondent.',
          exampleLabel: 'Asia/Kolkata',
        },
        {
          key: 'uniqueTimestamp',
          description: 'Unique high-resolution timestamp string.',
          exampleLabel: '17425626001234',
        },
      ],
    },
    {
      title: 'Random',
      description:
        'Generated values useful for unique codes, OTPs, and identifiers.',
      variables: [
        {
          key: 'randomUuid',
          description: 'Random UUID (v4).',
          exampleLabel: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
        },
        {
          key: 'randomOtp6',
          description: 'Random 6-digit numeric OTP.',
          exampleLabel: '482910',
        },
        {
          key: 'randomNumber',
          description: 'Random integer from 0 to 999999.',
          exampleLabel: '384729',
        },
        {
          key: 'randomHex8',
          description: 'Random 8-character hexadecimal string.',
          exampleLabel: 'a3f8c2d1',
        },
        {
          key: 'randomAlphanumeric',
          description: 'Random 8-character alphanumeric string.',
          exampleLabel: 'Xk9mP2nQ',
        },
      ],
    },
    {
      title: 'Respondent',
      description:
        'Identity of the person filling out the form, when available.',
      variables: [
        {
          key: 'respondentId',
          description: 'Authenticated or session respondent id.',
          exampleLabel: '507f1f77bcf86cd799439011',
        },
        {
          key: 'respondentEmail',
          description: 'Respondent email address when available.',
          exampleLabel: 'jane@example.com',
        },
        {
          key: 'respondentName',
          description: 'Respondent display name when available.',
          exampleLabel: 'Jane Doe',
        },
      ],
    },
    {
      title: 'Submission',
      description: 'Values tied to the current form submission.',
      variables: [
        {
          key: 'submissionId',
          description: 'Id of the current form submission.',
          exampleLabel: 'Set when the form is submitted',
        },
        {
          key: 'submissionDate',
          description: 'Date and time when the current submission was created.',
          exampleLabel: 'Set when the form is submitted',
        },
      ],
    },
  ];

  copyKey(key: string): void {
    void navigator.clipboard.writeText(key).then(
      () =>
        this.toasterService.success('You can paste it wherever you need it.', {
          title: 'Key copied',
        }),
      () =>
        this.toasterService.error('Please try again.', {
          title: 'Could not copy key',
        }),
    );
  }
}
